/**
 * useGeometry Hook
 *
 * Detects gsp:hasGeometry in entity properties, then fetches gsp:asWKT
 * from the geometry nodes using the public /describe endpoint. DIM has one
 * geometry per CRS (bno:inCRS), each with one WKT literal.
 */

import { useState, useEffect } from 'react';
import { wktToGeoJSON } from 'betterknown';
import { describeEntity } from '@services/sparql.service';
import type { EntityObject, EntityProperty } from '@/types';

const GSP_NS = 'http://www.opengis.net/ont/geosparql#';
const HAS_GEOMETRY = GSP_NS + 'hasGeometry';
const AS_WKT = GSP_NS + 'asWKT';
const EPSG_IRI_RE = /<http:\/\/www\.opengis\.net\/def\/crs\/EPSG\//i;

export interface GeometryResult {
  wkt: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Returns true if the given WKT literal can be parsed by betterknown.
 * Some WKT literals (3D/ZM) are not supported by the parser; we try each
 * candidate and pick the first that succeeds.
 */
function isParseableWkt(value: string): boolean {
  try {
    return wktToGeoJSON(value) != null;
  } catch {
    return false;
  }
}

/**
 * Picks the WKT literal to draw from the asWKT values of the geometries of an
 * entity, one per CRS. Prefer the literals without an EPSG CRS IRI prefix
 * (CRS84, lon/lat) and pick the first candidate that the WKT parser can
 * actually parse, since some 3D/ZM variants are unsupported. Without one,
 * take an EPSG literal; EntityMap reprojects it.
 */
export function pickWkt(objects: EntityObject[]): string | null {
  const literals = objects.filter((o) => o.type === 'literal');
  const nonEpsg = literals.filter((o) => !EPSG_IRI_RE.test(o.value));
  const wktObj = nonEpsg.find((o) => isParseableWkt(o.value)) ?? nonEpsg[0] ?? literals[0];
  return wktObj?.value ?? null;
}

/**
 * Extracts the WKT literal from entity properties.
 *
 * Flow:
 *  1. Find the gsp:hasGeometry objects (URIs of the geometry nodes, one per CRS)
 *  2. Fetch those nodes via /describe
 *  3. Pick one of their gsp:asWKT literal values (pickWkt)
 */
export function useGeometry(properties: EntityProperty[]): GeometryResult {
  const [wkt, setWkt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const geometryProp = properties.find((p) => p.predicate === HAS_GEOMETRY);
    const geometryUris = (geometryProp?.objects ?? [])
      .filter((o) => o.type === 'uri')
      .map((o) => o.value);

    if (geometryUris.length === 0) {
      setWkt(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all(geometryUris.map((uri) => describeEntity(uri)))
      .then((nodes) => {
        if (cancelled) return;

        const wkts = nodes.flatMap(
          (geomProperties) => geomProperties.find((p) => p.predicate === AS_WKT)?.objects ?? []
        );
        setWkt(pickWkt(wkts));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[useGeometry] Failed to fetch geometry:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [properties]);

  return { wkt, loading, error };
}
